import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { api, workspaceSlug, type Bootstrap } from './api'
import { normalizeBrand, type Brand } from './brand'
import { loadCache, saveCache } from './cache'
import { type Actor, type CrumEvent, type Draft } from './events'
import { newId } from './ids'
import { type Config } from './model'
import { applyEvent, emptyState, type State } from './reducer'

/**
 * The same contract the General Text build exposed, so every component and route
 * carries over untouched — `brand` is the only addition, and it is additive.
 *
 * What changed is entirely underneath. There used to be per-shard watches, a
 * CRDT-safe fold cursor, a write queue that re-read the freshest file content
 * before every append, and a legacy-import pass. All of it existed to make an
 * append-only log survive a character-level CRDT it did not want. A server that
 * assigns order deletes the lot: append, receive, fold.
 */
interface StoreValue {
  ready: boolean
  me: Actor | null
  /** Whether this session may edit the brand and the workspace's settings.
   *  Decided by the server from the roster; the flag is only for the UI. */
  admin: boolean
  state: State
  version: number
  config: Config
  brand: Brand
  /** The public demo. Real code and real data, wiped on a schedule — the only
   *  thing the UI does with it is say so, loudly and everywhere. */
  demo: boolean
  dispatch: (drafts: Draft | Draft[]) => Promise<void>
  saveConfig: (next: Config) => Promise<void>
  saveBrand: (next: Brand) => Promise<void>
}

const StoreContext = createContext<StoreValue | null>(null)

/** One cache per origin AND per workspace. Path-scoped workspaces share an origin,
 *  so a key on the origin alone would hand one team's projection to another's
 *  tab in the same browser. The store validates the key on read as well. */
function cacheKey(): string {
  const origin = typeof location === 'undefined' ? 'local' : location.origin
  return `${origin}/w/${workspaceSlug()}`
}

/** Reconnect backoff: quick enough to feel instant on a flaky connection,
 *  capped so a deployment that is genuinely down isn't hammered. */
const RECONNECT_MS = [500, 1000, 2000, 5000, 10000, 15000]

export function StoreProvider({ boot, children }: { boot: Bootstrap; children: ReactNode }) {
  const stateRef = useRef<State>(emptyState())
  /** Highest server seq folded in. The whole sync protocol is this number. */
  const seqRef = useRef(0)
  const meRef = useRef<Actor | null>({ id: boot.me.sub, name: boot.me.name })
  const socketRef = useRef<WebSocket | null>(null)
  const bumpScheduled = useRef(false)
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [version, setVersion] = useState(0)
  const [ready, setReady] = useState(false)
  const [config, setConfig] = useState<Config>(boot.config)
  const [brand, setBrand] = useState<Brand>(normalizeBrand(boot.brand))

  function bump() {
    if (bumpScheduled.current) return
    bumpScheduled.current = true
    queueMicrotask(() => {
      bumpScheduled.current = false
      setVersion((v) => v + 1)
    })
  }

  function schedulePersist() {
    if (persistTimer.current) clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(() => {
      void saveCache(cacheKey(), stateRef.current, seqRef.current)
    }, 1000)
  }

  /** Fold a batch and advance the cursor. applyEvent dedupes by event id, so a
   *  replayed or overlapping batch is a no-op — which is what lets the socket be
   *  at-least-once and the catch-up ranges overlap without coordination. */
  function absorb(events: Array<CrumEvent & { seq?: number }>) {
    if (events.length === 0) return
    for (const ev of events) {
      applyEvent(stateRef.current, ev)
      if (typeof ev.seq === 'number' && ev.seq > seqRef.current) seqRef.current = ev.seq
    }
    bump()
    schedulePersist()
  }

  useEffect(() => {
    let disposed = false
    let attempt = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null

    function connect() {
      if (disposed) return
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const ws = new WebSocket(
        `${proto}//${location.host}/api/w/${encodeURIComponent(workspaceSlug())}/stream?since=${seqRef.current}`,
      )
      socketRef.current = ws

      ws.onopen = () => {
        attempt = 0
      }
      ws.onmessage = (e) => {
        if (disposed) return
        try {
          const msg = JSON.parse(String(e.data)) as {
            type: string
            events?: Array<CrumEvent & { seq: number }>
            seq?: number
          }
          if (msg.type === 'events' && msg.events) absorb(msg.events)
          else if (msg.type === 'ready') setReady(true)
          // The demo reset the log out from under us. Our cursor and our cached
          // projection both describe events that no longer exist, and no amount
          // of catching up fixes that, so throw both away and start over.
          else if (msg.type === 'reset') {
            try {
              indexedDB.deleteDatabase('crum')
            } catch {
              // Blocked or unavailable; the reload still lands on a fresh log.
            }
            location.reload()
          }
        } catch {
          // A frame we can't parse is skipped; the next catch-up re-delivers it.
        }
      }
      ws.onclose = () => {
        if (disposed) return
        // Reconnecting with the current seq IS the catch-up: whatever was
        // appended while the socket was down arrives before live events resume.
        const delay = RECONNECT_MS[Math.min(attempt, RECONNECT_MS.length - 1)]!
        attempt += 1
        reconnectTimer = setTimeout(connect, delay)
      }
      ws.onerror = () => ws.close()
    }

    async function start() {
      // Cached projection first, for an instant first paint.
      const cached = await loadCache(cacheKey())
      if (disposed) return
      if (cached) {
        stateRef.current = cached.state
        seqRef.current = cached.seq
        bump()
      }

      // Catch up over HTTP before opening the socket, so a large first load
      // arrives in pages rather than as one enormous frame.
      try {
        for (;;) {
          const page = await api.events(seqRef.current)
          if (disposed) return
          absorb(page.events)
          if (!page.more) break
        }
      } catch {
        // Offline or a hiccup: the socket's own catch-up covers the same ground.
      }
      if (disposed) return
      setReady(true)
      connect()
    }

    void start()
    return () => {
      disposed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (persistTimer.current) clearTimeout(persistTimer.current)
      socketRef.current?.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Append. The event is applied locally first so the UI never waits on a round
   * trip; the server echo folds to a no-op on the id dedupe.
   *
   * The `actor` set here is only what the optimistic view shows — the server
   * overwrites it from the session, so it is not a claim, and the authoritative
   * copy arrives with the echo.
   */
  async function dispatchImpl(drafts: Draft | Draft[]): Promise<void> {
    const list = Array.isArray(drafts) ? drafts : [drafts]
    if (list.length === 0) return
    const now = new Date().toISOString()
    const events: CrumEvent[] = list.map((d) => ({
      id: newId('evt'),
      ts: now,
      actor: meRef.current,
      type: d.type,
      subject: d.subject,
      ...(d.data ? { data: d.data } : {}),
    }))

    for (const ev of events) applyEvent(stateRef.current, ev)
    bump()
    schedulePersist()

    await api.append(events)
  }

  async function saveConfigImpl(next: Config): Promise<void> {
    setConfig(next)
    await api.saveConfig(next)
  }

  async function saveBrandImpl(next: Brand): Promise<void> {
    const saved = await api.saveBrand(next)
    setBrand(saved)
  }

  const value = useMemo<StoreValue>(
    () => ({
      ready,
      me: meRef.current,
      admin: boot.me.admin,
      demo: boot.demo,
      state: stateRef.current,
      version,
      config,
      brand,
      dispatch: dispatchImpl,
      saveConfig: saveConfigImpl,
      saveBrand: saveBrandImpl,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, version, config, brand, boot.me.admin, boot.demo],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext)
  if (!ctx) throw new Error('useStore must be used within StoreProvider')
  return ctx
}
